import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { dataUrltoBlob, getDimensions, resize } from '../../../utils/image'
import { InternalReport, InternalReportMutation, InternalReportStatus, InternalReportVisibility } from '../types/InternalReport'

export const reportLanguages = ['en', 'ja', 'zh'] as const

export type ReportLanguage = (typeof reportLanguages)[number]

export type ReportTranslations = Record<ReportLanguage, string>

export const toTranslations = (map?: ContentLanguageTranslation | null): ReportTranslations => ({
  en: map?.en ?? '',
  ja: map?.ja ?? '',
  zh: map?.zh ?? '',
})

export type ReportFormValues = {
  titles: ReportTranslations
  summaries: ReportTranslations
  analysts: string[]
  fileId: string
  tags: string[]
  gameIds: string[]
  coverUrls: string[]
  visibility: InternalReportVisibility
  allowedUsers: string[]
  published: boolean
  // Milliseconds, so an untouched stored value is sent back exactly.
  publishedAt: number
}

export type ReportField =
  | 'titles'
  | 'summaries'
  | 'analysts'
  | 'fileId'
  | 'tags'
  | 'gameIds'
  | 'coverUrls'
  | 'visibility'
  | 'allowedUsers'
  | 'published'
  | 'publishedAt'

export const reportFieldLabelKeys: Record<ReportField, string> = {
  titles: 'internal-analysts:reports_title',
  summaries: 'internal-analysts:reports_summary',
  analysts: 'internal-analysts:reports_analysts',
  fileId: 'internal-analysts:reports_file',
  tags: 'internal-analysts:reports_tags',
  gameIds: 'internal-analysts:reports_related_games',
  coverUrls: 'internal-analysts:reports_covers',
  visibility: 'internal-analysts:reports_visibility',
  allowedUsers: 'internal-analysts:reports_allowed_users',
  published: 'internal-analysts:reports_published',
  publishedAt: 'internal-analysts:reports_published_at',
}

export const reportVisibilityOptions: { value: InternalReportVisibility; labelKey: string; helpKey: string }[] = [
  {
    value: InternalReportVisibility.Teaser,
    labelKey: 'internal-analysts:reports_visibility_teaser',
    helpKey: 'internal-analysts:reports_visibility_teaser_help',
  },
  {
    value: InternalReportVisibility.Private,
    labelKey: 'internal-analysts:reports_visibility_private',
    helpKey: 'internal-analysts:reports_visibility_private_help',
  },
  {
    value: InternalReportVisibility.Public,
    labelKey: 'internal-analysts:reports_visibility_public',
    helpKey: 'internal-analysts:reports_visibility_public_help',
  },
]

export const reportStatusLabelKeys: Record<InternalReportStatus, string> = {
  published: 'internal-analysts:reports_status_published',
  scheduled: 'internal-analysts:reports_status_scheduled',
  draft: 'internal-analysts:reports_status_draft',
}

export const hasAllowedUsers = (visibility: InternalReportVisibility) => visibility !== InternalReportVisibility.Public

const unique = (values: string[]) => Array.from(new Set(values))

export const toReportFormValues = (report: InternalReport | undefined, now = Date.now()): ReportFormValues => ({
  titles: toTranslations(report?.title),
  summaries: toTranslations(report?.comment),
  analysts: report?.analysts ?? [],
  fileId: report?.fileId ?? '',
  tags: report?.tags ?? [],
  gameIds: report?.gameIds ?? [],
  coverUrls: report?.coverUrls ?? [],
  visibility: (report?.visibility as InternalReportVisibility) ?? InternalReportVisibility.Private,
  allowedUsers: report?.allowedUsers ?? [],
  published: !!report?.published,
  publishedAt: report?.published && report.publishedAt ? report.publishedAt : now,
})

// Legacy's body and key order; a draft is sent with the save time (the backend stores 0).
export const toReportMutation = (report: InternalReport | undefined, values: ReportFormValues, now = Date.now()): InternalReportMutation => ({
  title: values.titles,
  coverUrls: values.coverUrls,
  fileId: values.fileId,
  analysts: unique(values.analysts),
  comment: values.summaries,
  gameIds: values.gameIds,
  tags: values.tags,
  publishedAt: values.published ? values.publishedAt : now,
  published: values.published,
  visibility: values.visibility,
  ...(hasAllowedUsers(values.visibility) ? { allowedUsers: values.allowedUsers } : {}),
  ...(report ? { id: report.id } : {}),
})

const translationsEqual = (a?: ContentLanguageTranslation, b?: ContentLanguageTranslation) => {
  const left = toTranslations(a)
  const right = toTranslations(b)
  return reportLanguages.every((language) => left[language] === right[language])
}

const listsEqual = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((value, index) => value === b[index])

export const getUnstoredReportFields = (sent: InternalReportMutation, stored: InternalReport): ReportField[] => {
  const checks: [ReportField, boolean][] = [
    ['titles', translationsEqual(sent.title, stored.title)],
    ['summaries', translationsEqual(sent.comment, stored.comment)],
    ['analysts', listsEqual(sent.analysts, stored.analysts)],
    ['fileId', sent.fileId === stored.fileId],
    ['tags', listsEqual(sent.tags, stored.tags)],
    ['gameIds', listsEqual(sent.gameIds, stored.gameIds)],
    ['coverUrls', listsEqual(sent.coverUrls, stored.coverUrls)],
    ['visibility', sent.visibility === stored.visibility],
    ['allowedUsers', listsEqual(sent.allowedUsers ?? [], stored.allowedUsers)],
    ['published', sent.published === !!stored.published],
    ['publishedAt', !sent.published || sent.publishedAt === stored.publishedAt],
  ]
  return checks.filter(([, equal]) => !equal).map(([field]) => field)
}

export const getReportStatus = (report: Pick<InternalReport, 'published' | 'publishedAt'>, now = Date.now()): InternalReportStatus => {
  if (!report.published) return 'draft'
  return report.publishedAt <= now ? 'published' : 'scheduled'
}

// Publishing a report, or changing who sees a published one, reaches customers.
export const needsPublishConfirmation = (report: InternalReport | undefined, values: ReportFormValues) => {
  if (!values.published) return false
  if (!report?.published) return true
  return values.visibility !== report.visibility
}

export const sortReports = (reports: InternalReport[]) =>
  [...reports].sort((a, b) => {
    if (a.published !== b.published) return a.published ? -1 : 1
    if (a.published && a.publishedAt !== b.publishedAt) return b.publishedAt - a.publishedAt
    return (a.title?.en ?? '').localeCompare(b.title?.en ?? '')
  })

export const filterReports = (reports: InternalReport[], search: string) => {
  const query = search.trim().toLowerCase()
  if (!query) return reports
  return reports.filter((report) => reportLanguages.some((language) => report.title?.[language]?.toLowerCase().includes(query)))
}

export const isValidEmail = (email: string) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)

export const MAX_REPORT_COVER_SIZE = 2000

export const MAX_REPORT_COVER_BYTES = 5 * 1000 * 1000

export const needsReportCoverConversion = ({ width, height, size, type }: { width: number; height: number; size: number; type: string }) =>
  width > MAX_REPORT_COVER_SIZE || height > MAX_REPORT_COVER_SIZE || size > MAX_REPORT_COVER_BYTES || type !== 'image/jpeg'

export const toJpegFileName = (name: string) => `${name.replace(/\.[^.]+$/, '')}.jpg`

// Legacy's cover rule: anything large or not a JPEG becomes a JPEG of at most 2000×2000 px.
export const prepareReportCover = async (file: File) => {
  const { width, height } = await getDimensions(file)
  if (!needsReportCoverConversion({ width, height, size: file.size, type: file.type })) return file
  const dataUrl = await resize(file, { width: MAX_REPORT_COVER_SIZE, height: MAX_REPORT_COVER_SIZE, quality: 0.9, mimeType: 'image/jpeg' })
  return new File([dataUrltoBlob(dataUrl)], toJpegFileName(file.name), { type: 'image/jpeg' })
}

export const moveItem = <T>(items: T[], from: number, to: number) => {
  if (to < 0 || to >= items.length) return items
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}
